/**
 * Receipts inside the donor sheet (GH-856 e).
 *
 * The donor's own gifts and their receipts, from `/v1/donor/gifts` — the list the iPhone app's
 * Receipts screen reads, drawn the way its gift receipt is (`GiftReceiptView`): the same lines, in
 * the same order, with the same figures from server minor units. Settled gifts only; a pledge is
 * not a gift and never appears here. Nothing new on the server.
 *
 * Its own file to keep `DonorPanel.tsx` under the size ceiling; that sheet is its only host.
 * Composed from existing parts only: the sheet's option rows (`donor__option`) for the list and the
 * receipt's ledger rows (`totals` / `row` / `row--total`) for one gift's lines.
 */
import { useEffect, useState } from "react";
import { ANONYMITY_COPY, formatMoney, type DonorGift } from "@givehere/contracts";
import { fetchDonorGifts } from "./donor-session.js";

/** "General fund" is the always-on fund, not an occasion: the iPhone draws no event line for it. */
const isGeneralFundName = (name: string) => name.trim().toLowerCase() === "general fund";

export function ReceiptsStep({ token, onBack }: { token: string; onBack: () => void }) {
  const [gifts, setGifts] = useState<readonly DonorGift[] | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchDonorGifts(token)
      .then((page) => {
        if (cancelled) return;
        setGifts(page.gifts);
        setHasMore(page.hasMore);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const more = () => {
    const last = gifts?.[gifts.length - 1];
    if (!last) return;
    setLoadingMore(true);
    fetchDonorGifts(token, last.givenAt)
      .then((page) => {
        setGifts((current) => [...(current ?? []), ...page.gifts]);
        setHasMore(page.hasMore);
      })
      .catch(() => setFailed(true))
      .finally(() => setLoadingMore(false));
  };

  return (
    <>
      <h2 id="donor-title" className="donor__title">
        Receipts
      </h2>

      {gifts === null && !failed && <p className="notice">Loading…</p>}
      {failed && (
        <p className="notice notice--error" role="alert">
          Your receipts didn't load.
        </p>
      )}
      {gifts !== null && gifts.length === 0 && <p className="donor__where">No gifts yet.</p>}

      {gifts !== null && gifts.length > 0 && (
        <ul className="donor__options">
          {gifts.map((gift) => {
            const expanded = open === gift.donationId;
            const purpose = isGeneralFundName(gift.eventName)
              ? gift.designationName
              : gift.eventName;
            return (
              <li key={gift.donationId}>
                <button
                  type="button"
                  className="donor__option"
                  aria-expanded={expanded}
                  onClick={() => setOpen(expanded ? null : gift.donationId)}
                >
                  <span className="donor__option-title" dir="auto">
                    {gift.organizationDisplayName}
                  </span>
                  <span className="donor__closed">
                    {new Date(gift.givenAt).toLocaleDateString()}
                    {purpose ? ` · ${purpose}` : ""}
                  </span>
                  <span className="receipts__amount">{formatMoney(gift.donation)}</span>
                </button>
                {expanded && <GiftReceipt gift={gift} />}
              </li>
            );
          })}
        </ul>
      )}

      {hasMore && (
        <button type="button" className="link" disabled={loadingMore} onClick={more}>
          {loadingMore ? "Loading…" : "Show older gifts"}
        </button>
      )}

      <div className="donor__actions">
        <button type="button" className="link" onClick={onBack}>
          Back
        </button>
      </div>
    </>
  );
}

/** One gift's receipt lines, in `GiftReceiptView`'s order. */
function GiftReceipt({ gift }: { gift: DonorGift }) {
  const event = isGeneralFundName(gift.eventName) ? null : gift.eventName;
  const supported = BigInt(gift.costSupport.amountMinor) > 0n;
  return (
    <section className="totals receipts__lines" aria-label="Receipt">
      <div className="row">
        <span>Date</span>
        <span>{new Date(gift.givenAt).toLocaleString()}</span>
      </div>
      {event && (
        <div className="row">
          <span>Event</span>
          <span dir="auto">{event}</span>
        </div>
      )}
      {gift.designationName && (
        <div className="row">
          <span>For</span>
          <span dir="auto">{gift.designationName}</span>
        </div>
      )}
      <div className="row">
        <span>Organization</span>
        <span dir="auto">{gift.organizationLegalName}</span>
      </div>
      <div className="row">
        <span>Gift</span>
        <span>{formatMoney(gift.donation)}</span>
      </div>
      {supported && (
        <>
          <div className="row">
            <span>Processing fee</span>
            <span>{formatMoney(gift.costSupport)}</span>
          </div>
          <div className="row row--total">
            <span>Charged</span>
            <span>{formatMoney(gift.total)}</span>
          </div>
        </>
      )}
      {gift.receiptNumber && (
        <div className="row">
          <span>Receipt</span>
          <span>{gift.receiptNumber}</span>
        </div>
      )}
      {gift.isAnonymous && <p className="donor__closed">{ANONYMITY_COPY.givenAnonymously}</p>}
    </section>
  );
}
